import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/navigation';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.slug !== article.slug && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Header />
        <main className="pt-24 pb-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              <span>返回首页</span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '91导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '91导航',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://91daohang.com/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `如何高效使用${article.categoryName}类网站？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以在91导航的${article.categoryName}分类下浏览我们精心筛选的优质网站，每个网站都有详细的介绍和标签，帮助您快速找到需要的服务。`,
        },
      },
      {
        '@type': 'Question',
        name: '91导航如何筛选和推荐网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的编辑团队从内容质量、用户体验、安全性和实用性等多个维度综合评估每一个网站，确保推荐的网站安全可靠、真正有用。',
        },
      },
    ],
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <time className="text-xs text-foreground-400">{article.date}</time>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </header>

          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed
              prose-strong:text-foreground-900
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
              prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              pb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="py-6 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                {article.categoryName}
              </span>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100/50 py-10 md:py-14 mt-10">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.slug}
                    slug={ra.slug}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    date={ra.date}
                    categoryName={ra.categoryName}
                    categorySlug={ra.category}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
    </>
  );
}